/**
 * An interface that conforms to the OneSignal Web SDK Logging library.
 */
interface OneSignalLog {
  setLevel(level: string): void;
}

/**
 * The object passed to the OneSignal.init function.
 */
interface OneSignalOptions {
  appId: string;
}

/**
 * Represents the different states the native permission prompt may have.
 * * default is when the user has not granted notification permissions yet or dismissed the prompt.
 * * granted is when the user granted the notification permissions.
 * * denied is when the user has blocked notifications.
 */
export enum NotificationPermission {
  Default = "default",
  Granted = "granted",
  Denied = "denied"
}

/**
 * The states for the remote resource response
 * Loaded is if the resource was fetched successfully
 * Failed is if the resource failed loading.
 */
export enum ResourceLoadState {
  Loaded,
  Failed
}

interface DynamicResourceLoader {
  loadSdkStylesheet(): Promise<ResourceLoadState>;
}

/**
 * The object that contains the OneSignal context content
 */
interface OneSignalContext {
  dynamicResourceLoader: DynamicResourceLoader;
}

/**
 * The object that contains the OneSignal environment methods
 */
interface OneSignalEnvironment {
  getOneSignalApiUrl(): Location;
}

/**
 * The type for the various callbacks.
 */
type Action<T> = (item: T) => void;

/**
 * An interface that conforms to the OneSignal Web SDK library.
 */
export default interface OneSignal {
  push(item: Function | object[]): void;
  init: (options: OneSignalOptions) => Promise<void>;
  setExternalUserId: (externalUserId?: string | null) => Promise<void>;
  getExternalUserId(): Promise<string | undefined | null>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendTag(key: string, value: any, callback?: Action<object>): Promise<object | null>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendTags(tags: {[key: string]: any}, callback?: Action<object>): Promise<object | null>;
  getTags(callback?: Action<object>): Promise<object | null>;
  deleteTag(tag: string): Promise<Array<string>>;
  deleteTags(tags: Array<string>, callback?: Action<Array<string>>): Promise<Array<string>>;
  sendOutcome(outcomeName: string, outcomeWeight?: number | undefined): Promise<void>;
  isPushNotificationsEnabled(): Promise<boolean>;
  getNotificationPermission(): Promise<NotificationPermission>;
  registerForPushNotifications(): Promise<void>;
  log: OneSignalLog;
  context: OneSignalContext;
  sdkEnvironment: OneSignalEnvironment;
}
